import React, { useEffect, useRef, useState } from "react";
import styles from "../../_styles/Choose.module.scss";
import { ISectionItemProps } from "../../_types/interfaces";
import useInViewport from "../../../../hooks/useInViewPort";
import ReactPlayer from "react-player";
import MobileBottomInfoBlock from "../MobileComponents/MobileBottomInfoBlock";
import MobileProgressController from "../MobileComponents/MobileProgressController";
import useInterfaceContext from "../../../../components/providers/interface";
import { useDispatch } from "react-redux";
import {
  setCurrentItem,
  setCurrentVideoId,
  setIsAds,
  setLastVisitedId,
} from "../../../../store/slices/currentItem.slice";
import { ROUTES } from "../../../../constants/routes";
import { useNavigate } from "react-router-dom";
import FiltersHeader from "../FiltersHeader/FiltersHeader";
import { useGetCurrentVideoQuery } from "../../../../store/api/filmAPI";
import { useTranslation } from "react-i18next";
import { languageHandler } from "../../../../utils/helpers";
import { AdSkip } from "../../../../components/shared/AdsSkip/AdsSkip";
import AdsBottomMenu from "../playerController/AdsBottomMenu";
import Volume from "../../../../components/icons/Volume";
import Muted from "../../../../components/icons/Muted";
import { useAppSelector } from "../../../../store/store";
import { setMutedState } from "../../../../store/slices/filters.slice";

const MobilePlayer = ({
  item,
  horizontal,
  currentId,
  filterErrorItem,
  skipAdsHandler,
  onTrailerWatched,
}: ISectionItemProps) => {
  const [url, setUrl] = useState("");
  const [player, setPlayer] = useState(null);
  const [time, setTime] = useState<number | number[]>(0);
  const [maxValue, setMaxValue] = useState<number | number[]>(0);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muteTouched, setMuteTouched] = useState(false);
  const errorHandledRef = useRef(false);
  const dispatch = useDispatch();
  const muted = useAppSelector((state) => state.filters.mutedState);
  const playerMuted = muteTouched ? muted : true;
  const navigate = useNavigate();

  const { i18n } = useTranslation();
  const { isMobile } = useInterfaceContext();
  const { inViewport, targetRef } = useInViewport();

  const { data } = useGetCurrentVideoQuery(
    {
      queries: { language: languageHandler(i18n.language) },
      id: item.id,
    },
    {
      skip: item.commercial_url ? true : !inViewport,
    },
  );

  const clickHandler = () => {
    if (ready) {
      navigate(`${ROUTES.DETAILS}/${item.id}`);
      dispatch(setLastVisitedId(item.id as number));
    }
  };

  const togglePlayback = () => {
    const youtubePlayer = player as any;

    if (playing) {
      youtubePlayer?.pauseVideo?.();
      setPlaying(false);
      return;
    }

    youtubePlayer?.playVideo?.();
    setPlaying(true);
  };

  useEffect(() => {
    errorHandledRef.current = false;

    if (!item.commercial_url) {
      const trailer = data?.results.find((item: any) => item.type === "Trailer") || data?.results[0];
      setUrl(trailer ? trailer?.key : "");

      if (inViewport && data?.results && !trailer) {
        errorHandledRef.current = true;
        filterErrorItem(item.id);
      }
    } else {
      setUrl(item.commercial_url);
    }
  }, [data, filterErrorItem, inViewport, item]);

  const handlePlayerError = () => {
    if (errorHandledRef.current) return;

    errorHandledRef.current = true;
    filterErrorItem(item.id);
  };

  useEffect(() => {
    if (inViewport) {
      dispatch(setCurrentVideoId(item.id));
      dispatch(setCurrentItem(item));
      dispatch(setIsAds(!!item.commercial_url));
    }
    if (!inViewport) {
      setReady(false);
      setPlaying(true);
      setMuteTouched(false);
    }
  }, [inViewport, item, dispatch]);
  const dirData = i18n.language === "ar" ? "rtl" : "ltr";

  return (
    <div ref={targetRef} className={styles.itemWrapper}>
      {!horizontal && <FiltersHeader />}
      {inViewport && (
        <>
          <div dir={dirData} className={`${styles.player} ${horizontal && styles.playerHorizontal}`}>
            {item?.commercial_url && inViewport && (
              <AdSkip horizontal={horizontal} onSkip={() => skipAdsHandler("down", item.id)} />
            )}
            {url ? (
              <ReactPlayer
                className={styles.mobilePlayerFrame}
                controls={false}
                width={"100%"}
                height={!horizontal ? "100%" : "130%"}
                playsinline={true}
                progressInterval={500}
                onProgress={(value: any) => setTime(Math.round(value.playedSeconds))}
                playing={playing}
                onPlay={() => setPlaying(true)}
                loop={true}
                style={!horizontal ? { background: "#000000" } : { position: "relative", top: "-30px" }}
                url={`https://www.youtube.com/watch?v=${url}`}
                muted={playerMuted}
                onPause={() => setPlaying(false)}
                onError={handlePlayerError}
                onReady={(e: any) => {
                  const youtubePlayer = e["player"];
                  setPlayer(youtubePlayer);
                  const duration = youtubePlayer.getDuration();
                  setMaxValue(duration);

                  onTrailerWatched(item.title, `https://www.youtube.com/watch?v=${url}`);

                  if (playerMuted) {
                    youtubePlayer?.mute?.();
                  }
                  youtubePlayer?.playVideo?.();
                  setPlaying(true);

                  setTimeout(() => {
                    setReady(true);
                  }, 200);
                }}
              />
            ) : (
              <div style={{ height: "350px", width: "100%", background: "#000000" }} />
            )}
            {url && !horizontal && (
              <button
                type="button"
                className={styles.mobileVideoToggle}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  togglePlayback();
                }}
                aria-label={playing ? "Pause video" : "Play video"}
              />
            )}
          </div>
          {!item.commercial_url && !horizontal && (
            <button
              type="button"
              className={styles.mobileMuteButton}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                const nextMuted = !playerMuted;
                setMuteTouched(true);
                dispatch(setMutedState(nextMuted));
                setPlaying(true);

                const youtubePlayer = player as any;
                if (nextMuted) {
                  youtubePlayer?.mute?.();
                } else {
                  youtubePlayer?.unMute?.();
                  youtubePlayer?.setVolume?.(100);
                }
                youtubePlayer?.playVideo?.();
              }}
              aria-label={playerMuted ? "Unmute" : "Mute"}
            >
              <span key={playerMuted ? "muted" : "unmuted"} className={styles.mobileMuteIcon}>
                {playerMuted ? <Muted /> : <Volume />}
              </span>
            </button>
          )}
          {item.commercial_url && !horizontal && <AdsBottomMenu />}
          {!item.commercial_url && (
            <div className={styles.bottomControlWrapper}>
              {!horizontal && (
              <MobileBottomInfoBlock
                item={item}
                currentItemId={item.id}
                voteCount={item.vote_count}
                isMobile={isMobile}
                clickHandler={clickHandler}
              />
              )}
              <div className={styles.mobileProgressController}>
                <MobileProgressController
                  maxValue={maxValue as number}
                  time={time as number}
                  player={player as any}
                  setTime={setTime}
                  isMobile={isMobile}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MobilePlayer;
